import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { IconButton2, Text, theme, useThemeName } from '@frameio/vapor';
import { IconX } from '@frameio/vapor-icons';
import styles from './UpgradeDialogLayout.module.css';

export const UPGRADE_DIALOG_STYLE: CSSProperties = {
  borderRadius: theme.radii[16],
  maxWidth: '900px',
  overflow: 'hidden',
};

const titleStyles: CSSProperties = {
  fontSize: 'var(--upgrade-dialog-title-font-size, 36px)',
  letterSpacing: '-0.01em',
  lineHeight: '125%',
};

type UpgradeDialogLayoutProps = {
  actions: ReactNode;
  badge?: ReactNode;
  children?: ReactNode;
  closeLabel: string;
  image: string;
  isWide?: boolean;
  mobileImage: string;
  onClose: () => void;
  subtitle?: ReactNode;
  title: ReactNode;
};

export function UpgradeDialogLayout({
  actions,
  badge,
  children,
  closeLabel,
  image,
  isWide = false,
  mobileImage,
  onClose,
  subtitle,
  title,
}: UpgradeDialogLayoutProps) {
  const isLightTheme = useThemeName() === 'light';
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = image;
    return () => {
      img.onload = null;
    };
  }, [image]);

  const imageStyle = {
    '--plan-image-bg': `url(${image})`,
    '--plan-image-bg-mobile': `url(${mobileImage})`,
    opacity: imageLoaded ? 1 : 0,
  } as CSSProperties;

  const blurBackgroundStyle = {
    '--plan-image-bg': `url(${image})`,
    '--blur-background-opacity': isLightTheme ? 0.3 : 0.75,
  } as CSSProperties;

  return (
    <div className={styles.container}>
      <div className={styles.closeButtonContainer}>
        <IconButton2
          icon={<IconX color={theme.colors.icon.primary} size={24} />}
          label={closeLabel}
          onPress={onClose}
          size="small"
          variant="tertiary"
        />
      </div>

      <div className={styles.row}>
        <div className={styles.imageColumn}>
          <div className={styles.planImage} style={imageStyle} />
        </div>

        <div className={styles.column} data-wide={isWide}>
          <div className={styles.headerContainer}>
            {badge}
            <div className={styles.title}>
              <Text color="primary" style={titleStyles} variant="titleSmall">
                {title}
              </Text>
            </div>
            {subtitle && (
              <Text color="bodyStark" fontWeight="regular" variant="header">
                {subtitle}
              </Text>
            )}
          </div>

          {children}

          <div className={styles.ctaContainer}>{actions}</div>
        </div>
      </div>
      <div className={styles.blurBackground} style={blurBackgroundStyle} />
    </div>
  );
}
